import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { budgetSettingsApi, preferencesApi } from '../../services/api';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  calculateBudgetRollover,
  monthIdToKey,
  ROLLOVER_CATEGORIES,
} from '../../utils/budgetRollover';
import { getCategoryStyle, CATEGORY_ORDER } from './categoryStyles';
import ExpensePlanV3 from './ExpensePlanV3';

const DEFAULT_BUDGETS = {
  'FOOD': 300, 'GO OUT': 300, 'BILLS': 2492.1, 'AMANDA': 100,
  'MARTIN': 100, 'HOLIDAYS': 150, 'HOUSE': 30, 'OTHERS': 100,
};
const DEFAULT_SAVINGS = { MARTIN: 0, AMANDA: 0 };
const ROLLOVER_PREFERENCE_KEY = 'budgetRollover';
const ROLLOVER_STORAGE_KEY = 'saver-budget-rollover';
const ROLLOVER_START_MONTH = '2026-08';

const normalizeRolloverSettings = (value) => ({
  startMonth: ROLLOVER_START_MONTH,
  adjustments: Object.fromEntries(ROLLOVER_CATEGORIES.map(category => {
    const stored = value?.adjustments?.[category];
    const validAdjustments = stored && typeof stored === 'object'
      ? Object.fromEntries(Object.entries(stored).filter(([, amount]) => Number.isFinite(Number(amount))))
      : {};
    return [category, validAdjustments];
  })),
});

const loadLocalRolloverSettings = () => {
  try {
    const saved = localStorage.getItem(ROLLOVER_STORAGE_KEY);
    return normalizeRolloverSettings(saved ? JSON.parse(saved) : null);
  } catch (error) {
    return normalizeRolloverSettings(null);
  }
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const monthKey = (date) => date.getFullYear() * 12 + date.getMonth();

const BudgetChartsV3 = ({ transactions, conversionRates }) => {
  const [budgets, setBudgets] = useState(() => {
    const saved = localStorage.getItem('saver-budgets');
    return saved ? JSON.parse(saved) : DEFAULT_BUDGETS;
  });
  const [savings, setSavings] = useState(() => {
    const saved = localStorage.getItem('saver-savings');
    return saved ? JSON.parse(saved) : DEFAULT_SAVINGS;
  });
  const [editing, setEditing] = useState(false);
  const [temp, setTemp] = useState({});
  const [today, setToday] = useState(() => new Date());
  const [selectedMonth, setSelectedMonth] = useState(() => startOfMonth(new Date()));
  const [rolloverSettings, setRolloverSettings] = useState(loadLocalRolloverSettings);
  const [rolloverSettingsLoaded, setRolloverSettingsLoaded] = useState(false);
  const firstLoad = useRef(true);
  const saveTimer = useRef(null);
  const rolloverSaveTimer = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setToday(new Date()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    budgetSettingsApi.get().then(settings => {
      if (settings?.budgets) {
        setBudgets(settings.budgets);
        localStorage.setItem('saver-budgets', JSON.stringify(settings.budgets));
      }
      if (settings?.savings) {
        setSavings(settings.savings);
        localStorage.setItem('saver-savings', JSON.stringify(settings.savings));
      }
      firstLoad.current = false;
    }).catch(() => { firstLoad.current = false; });
  }, []);

  useEffect(() => {
    let active = true;
    preferencesApi.get(ROLLOVER_PREFERENCE_KEY).then(saved => {
      if (!active) return;
      setRolloverSettings(normalizeRolloverSettings(saved));
      setRolloverSettingsLoaded(true);
    }).catch(() => {
      if (active) setRolloverSettingsLoaded(true);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!rolloverSettingsLoaded) return undefined;

    localStorage.setItem(ROLLOVER_STORAGE_KEY, JSON.stringify(rolloverSettings));
    clearTimeout(rolloverSaveTimer.current);
    rolloverSaveTimer.current = setTimeout(() => {
      preferencesApi.set(ROLLOVER_PREFERENCE_KEY, rolloverSettings);
    }, 600);

    return () => clearTimeout(rolloverSaveTimer.current);
  }, [rolloverSettings, rolloverSettingsLoaded]);

  useEffect(() => {
    if (firstLoad.current) return;
    localStorage.setItem('saver-budgets', JSON.stringify(budgets));
    localStorage.setItem('saver-savings', JSON.stringify(savings));
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      budgetSettingsApi.update(budgets, savings).catch(() => {});
    }, 800);
  }, [budgets, savings]);

  const currentMonth = startOfMonth(today);
  const selectedMonthKey = monthKey(selectedMonth);
  const currentMonthKey = monthKey(currentMonth);
  const isCurrentMonth = selectedMonthKey === currentMonthKey;

  const canGoNext = selectedMonthKey < currentMonthKey;
  const changeMonth = (amount) => {
    if (amount > 0 && !canGoNext) return;
    setSelectedMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + amount, 1));
  };

  const daysRemaining = useMemo(() => {
    if (!isCurrentMonth) return 0;
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return Math.max(0, last.getDate() - today.getDate());
  }, [isCurrentMonth, today]);

  const monthLabel = selectedMonth.toLocaleString('es-ES', { month: 'long' });
  const year = selectedMonth.getFullYear();
  const rolloverStartMonthKey = monthIdToKey(rolloverSettings.startMonth);

  const expensesByCategory = useMemo(() => {
    const exp = {};
    const y = selectedMonth.getFullYear(), m = selectedMonth.getMonth();
    (transactions || []).forEach(t => {
      if (!t || !t.date || !t.expenseType) return;
      const d = new Date(t.date);
      if (d.getFullYear() === y && d.getMonth() === m) {
        exp[t.expenseType] = (exp[t.expenseType] || 0) + (t.gbpAmount || 0);
      }
    });
    return exp;
  }, [transactions, selectedMonth]);

  const rows = useMemo(() => {
    return CATEGORY_ORDER.filter(c => budgets[c] !== undefined).map(c => {
      const baseBudget = budgets[c] || 0;
      const rollover = ROLLOVER_CATEGORIES.includes(c)
        ? calculateBudgetRollover({
            transactions,
            category: c,
            baseBudget,
            selectedMonth,
            startMonthKey: rolloverStartMonthKey,
            adjustments: rolloverSettings.adjustments[c],
          })
        : null;
      const budget = rollover ? rollover.availableBudget : baseBudget;
      const spent = expensesByCategory[c] || 0;
      const remaining = Math.max(0, budget - spent);
      const over = Math.max(0, spent - budget);
      const pct = budget > 0 ? (spent / budget) * 100 : (over > 0 ? 101 : 0);
      const perDay = (c === 'FOOD' || c === 'GO OUT') && remaining > 0 && daysRemaining > 0
        ? remaining / daysRemaining
        : null;
      return { category: c, baseBudget, budget, spent, remaining, over, pct, perDay, rollover };
    });
  }, [budgets, daysRemaining, expensesByCategory, rolloverSettings, rolloverStartMonthKey, selectedMonth, transactions]);

  const saveRolloverAdjustments = (category, updates) => {
    setRolloverSettings(previous => {
      const categoryAdjustments = { ...(previous.adjustments[category] || {}) };
      Object.entries(updates).forEach(([monthId, amount]) => {
        if (amount === null) delete categoryAdjustments[monthId];
        else categoryAdjustments[monthId] = amount;
      });

      return {
        ...previous,
        adjustments: {
          ...previous.adjustments,
          [category]: categoryAdjustments,
        },
      };
    });
  };

  const applyExpensePlanBudgets = useCallback((categoryBudgets) => {
    setBudgets(previous => ({ ...previous, ...categoryBudgets }));
  }, []);

  const totals = useMemo(() => {
    const budget = rows.reduce((s, r) => s + r.budget, 0);
    const spent = rows.reduce((s, r) => s + r.spent, 0);
    return { budget, spent, remaining: Math.max(0, budget - spent), pct: budget > 0 ? (spent / budget) * 100 : 0 };
  }, [rows]);

  const startEditing = () => {
    setTemp({
      ...Object.fromEntries(Object.entries(budgets).map(([k, v]) => [`b_${k}`, v])),
      ...Object.fromEntries(Object.entries(savings).map(([k, v]) => [`s_${k}`, v])),
    });
    setEditing(true);
  };
  const cancelEditing = () => { setEditing(false); setTemp({}); };
  const saveEditing = () => {
    const newB = {}, newS = {};
    Object.entries(temp).forEach(([k, v]) => {
      const num = parseFloat(v) || 0;
      if (k.startsWith('b_')) newB[k.slice(2)] = num;
      else if (k.startsWith('s_')) newS[k.slice(2)] = num;
    });
    setBudgets(prev => ({ ...prev, ...newB }));
    setSavings(prev => ({ ...prev, ...newS }));
    setEditing(false);
    setTemp({});
  };

  return (
    <div className="v3-card-dark rounded-3xl p-6 md:p-8 shadow-2xl">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <p className="text-xs font-semibold tracking-widest text-slate-400 uppercase mb-1">Presupuesto</p>
          <div className="flex items-center gap-2">
            <MonthButton direction="previous" onClick={() => changeMonth(-1)} />
            <h2 className="min-w-[150px] md:min-w-[180px] text-center text-2xl md:text-3xl font-bold text-white capitalize">
              {monthLabel} {year}
            </h2>
            <MonthButton direction="next" disabled={!canGoNext} onClick={() => changeMonth(1)} />
          </div>
          <p className="text-xs text-slate-500 mt-1 text-center">
            {isCurrentMonth ? `${daysRemaining} días restantes` : 'Mes finalizado'}
          </p>
        </div>
        {editing ? (
          <div className="flex gap-2">
            <button onClick={cancelEditing} className="px-4 py-2 rounded-full text-sm font-semibold bg-white/5 text-slate-300 hover:bg-white/10 transition">
              Cancelar
            </button>
            <button onClick={saveEditing} className="px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/20 hover:shadow-fuchsia-500/40 transition">
              Guardar
            </button>
          </div>
        ) : (
          <button onClick={startEditing} className="px-4 py-2 rounded-full text-sm font-semibold bg-white/5 text-slate-200 hover:bg-white/10 transition">
            Editar
          </button>
        )}
      </div>

      <ExpensePlanV3
        conversionRates={conversionRates}
        onApplyBudgets={applyExpensePlanBudgets}
      />

      <div className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-violet-600/20 to-fuchsia-600/20 border border-fuchsia-400/20 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-fuchsia-500/20 blur-3xl"></div>
        <div className="relative">
          <p className="text-xs uppercase tracking-widest text-fuchsia-200 mb-2">Gasto total del mes</p>
          <div className="flex items-baseline gap-2 mb-4">
            <p className="text-4xl md:text-5xl font-bold text-white tabular-nums">{formatCurrency(totals.spent)}</p>
            <p className="text-sm text-slate-300">de {formatCurrency(totals.budget)}</p>
          </div>
          <ProgressBar value={totals.pct} />
          <div className="flex justify-between mt-3 text-sm">
            <span className="text-slate-300">{totals.pct.toFixed(0)}% usado</span>
            <span className={totals.remaining > 0 ? 'text-emerald-300 font-semibold' : 'text-red-300 font-semibold'}>
              {totals.remaining > 0 ? `${formatCurrency(totals.remaining)} libres` : `${formatCurrency(totals.spent - totals.budget)} de más`}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map(r => (
          <BudgetCard
            key={r.category}
            row={r}
            editing={editing}
            tempValue={temp[`b_${r.category}`]}
            onTempChange={(val) => setTemp(prev => ({ ...prev, [`b_${r.category}`]: val }))}
            savings={savings[r.category]}
            tempSavings={temp[`s_${r.category}`]}
            onTempSavingsChange={(val) => setTemp(prev => ({ ...prev, [`s_${r.category}`]: val }))}
            onSaveRolloverAdjustments={saveRolloverAdjustments}
          />
        ))}
      </div>
    </div>
  );
};

const MonthButton = ({ direction, disabled, onClick }) => {
  const isPrevious = direction === 'previous';
  const label = isPrevious ? 'Ver mes anterior' : 'Ver mes siguiente';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="shrink-0 w-9 h-9 rounded-full bg-white/5 border border-white/10 text-slate-200 flex items-center justify-center hover:bg-white/10 hover:border-white/20 transition disabled:opacity-30 disabled:cursor-default disabled:hover:bg-white/5"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" className="w-5 h-5 fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d={isPrevious ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
      </svg>
    </button>
  );
};

const ProgressBar = ({ value }) => {
  const clamped = Math.min(100, value);
  const over = value > 100;
  return (
    <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${
          over ? 'bg-red-500' : value > 80 ? 'bg-gradient-to-r from-amber-400 to-orange-500' : 'bg-gradient-to-r from-emerald-400 to-teal-500'
        }`}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
};

const BudgetCard = ({ row, editing, tempValue, onTempChange, savings, tempSavings, onTempSavingsChange, onSaveRolloverAdjustments }) => {
  const s = getCategoryStyle(row.category);
  const hasSavings = row.category === 'MARTIN' || row.category === 'AMANDA';

  return (
    <div className="p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 transition">
      <div className="flex items-start gap-3 mb-3">
        <div className={`shrink-0 w-10 h-10 rounded-full ${s.bg} flex items-center justify-center text-white shadow-lg`}>
          {s.emoji}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white">{s.label}</p>
          <div className="flex items-baseline gap-1 mt-0.5">
            <span className="text-lg font-bold text-white tabular-nums">{formatCurrency(row.spent)}</span>
            {editing ? (
              <div className="flex items-center gap-1 ml-1">
                <span className="text-xs text-slate-500">de</span>
                <input
                  type="number"
                  step="0.01"
                  value={tempValue ?? row.baseBudget}
                  onChange={(e) => onTempChange(e.target.value)}
                  title="Presupuesto base mensual"
                  className="w-20 px-2 py-0.5 text-xs rounded-md bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-1 focus:ring-fuchsia-400"
                />
              </div>
            ) : (
              <span className="text-xs text-slate-400">de {formatCurrency(row.budget)}</span>
            )}
          </div>
        </div>
        {!editing && (
          <div className={`shrink-0 px-2 py-1 rounded-full text-[10px] font-bold ${
            row.pct > 100 ? 'bg-red-500/20 text-red-300' :
            row.pct > 80 ? 'bg-amber-500/20 text-amber-300' :
            'bg-emerald-500/20 text-emerald-300'
          }`}>
            {row.budget <= 0 ? 'Déficit' : `${row.pct.toFixed(0)}%`}
          </div>
        )}
      </div>

      <ProgressBar value={row.pct} />

      <div className="flex items-center justify-between mt-2 text-xs">
        {row.over > 0 ? (
          <span className="text-red-300 font-semibold">+{formatCurrency(row.over)} de más</span>
        ) : (
          <span className="text-slate-400">{formatCurrency(row.remaining)} libres</span>
        )}
        {row.perDay !== null && !editing && (
          <span className="text-slate-500">{formatCurrency(row.perDay)}/día</span>
        )}
      </div>

      {row.rollover && !editing && (
        <RolloverBreakdown row={row} onSaveAdjustments={onSaveRolloverAdjustments} />
      )}

      {hasSavings && editing && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <label className="text-[10px] uppercase tracking-widest text-slate-400">Ahorrado</label>
          <input
            type="number"
            step="0.01"
            value={tempSavings ?? savings ?? 0}
            onChange={(e) => onTempSavingsChange(e.target.value)}
            className="mt-1 w-full px-2 py-1 text-xs rounded-md bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-1 focus:ring-fuchsia-400"
          />
        </div>
      )}
      {hasSavings && !editing && savings > 0 && (
        <div className="mt-2 text-[11px] text-cyan-300">💰 Ahorrado: {formatCurrency(savings)}</div>
      )}
    </div>
  );
};

const RolloverBreakdown = ({ row, onSaveAdjustments }) => {
  const { baseBudget, carryOver, availableBudget, history } = row.rollover;
  const [editingAdjustments, setEditingAdjustments] = useState(false);
  const [drafts, setDrafts] = useState({});
  const calculationTitle = `Base ${formatCurrency(baseBudget)} ${carryOver >= 0 ? '+' : '−'} acumulado ${formatCurrency(Math.abs(carryOver))} = ${formatCurrency(availableBudget)}`;

  const startEditingAdjustments = () => {
    setDrafts(Object.fromEntries(history.map(entry => [entry.monthId, entry.change])));
    setEditingAdjustments(true);
  };

  const cancelEditingAdjustments = () => {
    setDrafts({});
    setEditingAdjustments(false);
  };

  const saveAdjustments = () => {
    const updates = Object.fromEntries(history.map(entry => {
      const parsed = Number.parseFloat(String(drafts[entry.monthId]).replace(',', '.'));
      const amount = Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : entry.automaticChange;
      return [entry.monthId, amount === entry.automaticChange ? null : amount];
    }));
    onSaveAdjustments(row.category, updates);
    setEditingAdjustments(false);
  };

  return (
    <details className="group mt-3 pt-3 border-t border-white/10">
      <summary
        title={calculationTitle}
        className="cursor-pointer list-none flex items-center justify-between gap-2 text-[11px] text-cyan-300 hover:text-cyan-200 transition"
      >
        <span>
          Base {formatCurrency(baseBudget)} · acumulado {carryOver >= 0 ? '+' : '−'}{formatCurrency(Math.abs(carryOver))}
        </span>
        <span className="shrink-0 group-open:rotate-180 transition-transform" aria-hidden="true">⌄</span>
      </summary>
      <div className="mt-3 rounded-xl bg-slate-950/40 border border-white/5 p-3 text-[11px] text-slate-300">
        <div className="flex justify-between gap-3 pb-2 mb-2 border-b border-white/5">
          <div>
            <span className="block">Presupuesto base mensual</span>
            <span className="block mt-0.5 text-[10px] text-slate-500">Acumulación desde agosto de 2026</span>
          </div>
          <span className="font-semibold text-white tabular-nums self-start">{formatCurrency(baseBudget)}</span>
        </div>
        {history.length > 0 ? (
          <>
            <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
              {history.map(entry => (
                <div key={entry.monthKey} className="flex items-center justify-between gap-3">
                  <span className="capitalize text-slate-400 min-w-0">
                    {entry.date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                    {entry.isManual
                      ? ' · ajustado'
                      : entry.spent === 0
                        ? ' · sin gastos'
                        : entry.change > 0
                          ? ' · sobró'
                          : entry.change < 0
                            ? ' · superó'
                            : ' · exacto'}
                  </span>
                  {editingAdjustments ? (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-slate-500">£</span>
                      <input
                        type="number"
                        step="0.01"
                        aria-label={`Ajuste de ${entry.date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}`}
                        value={drafts[entry.monthId] ?? entry.change}
                        onChange={(event) => setDrafts(previous => ({ ...previous, [entry.monthId]: event.target.value }))}
                        className="w-20 px-2 py-1 text-right rounded-md bg-white/10 border border-white/20 text-white tabular-nums focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      />
                      <button
                        type="button"
                        onClick={() => setDrafts(previous => ({ ...previous, [entry.monthId]: entry.automaticChange }))}
                        title={`Usar cálculo automático: ${formatCurrency(entry.automaticChange)}`}
                        className="px-2 py-1 rounded-md bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white transition"
                      >
                        Auto
                      </button>
                    </div>
                  ) : (
                    <span className={`font-medium tabular-nums shrink-0 ${entry.change < 0 ? 'text-red-300' : entry.change > 0 ? 'text-emerald-300' : 'text-slate-400'}`}>
                      {entry.change > 0 ? '+' : entry.change < 0 ? '−' : ''}{formatCurrency(Math.abs(entry.change))}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2 mt-2 border-t border-white/5">
              {editingAdjustments ? (
                <>
                  <button type="button" onClick={cancelEditingAdjustments} className="px-2.5 py-1 rounded-md bg-white/5 text-slate-400 hover:bg-white/10 transition">
                    Cancelar
                  </button>
                  <button type="button" onClick={saveAdjustments} className="px-2.5 py-1 rounded-md bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30 transition">
                    Guardar ajustes
                  </button>
                </>
              ) : (
                <button type="button" onClick={startEditingAdjustments} className="px-2.5 py-1 rounded-md bg-white/5 text-cyan-300 hover:bg-white/10 transition">
                  Editar acumulados
                </button>
              )}
            </div>
          </>
        ) : (
          <p className="text-slate-500">El acumulado comienza este mes. El saldo de agosto podrá ajustarse desde septiembre.</p>
        )}
        <div className="flex justify-between gap-3 pt-2 mt-2 border-t border-white/5 text-white">
          <span>Disponible este mes</span>
          <span className="font-bold tabular-nums">{formatCurrency(availableBudget)}</span>
        </div>
      </div>
    </details>
  );
};

export default BudgetChartsV3;
